import type React from 'react';
import { useCallback, useState, type FormEvent } from 'react';
import {
  createAdmin,
  deleteAdmin,
  listAdmins,
  resetAdminPassword,
  updateAdmin,
} from '../api/admin';
import { useAsync } from '../api/useAsync';
import { useAuth } from '../auth/AuthContext';
import {
  AsyncBoundary,
  ConfirmDialog,
  Modal,
  Pill,
  SecretReveal,
  relativeTime,
} from '../components/ui';
import type { AdminAccount, AdminRole } from '../types/admin';

export function AdminsPage(): React.JSX.Element {
  const { admin: me } = useAuth();
  const load = useCallback(() => listAdmins(), []);
  const { data, loading, error, reload } = useAsync(load);

  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState<{ title: string; value: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AdminAccount | null>(null);
  const [confirmReset, setConfirmReset] = useState<AdminAccount | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'A ação falhou.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1>Administradores</h1>
          <p className="page__subtitle">
            Super administradores podem criar, remover e redefinir senhas.
          </p>
        </div>
        <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
          Novo administrador
        </button>
      </header>

      {actionError ? <div className="banner banner--error">{actionError}</div> : null}

      <AsyncBoundary loading={loading && !data} error={error} onRetry={reload}>
        <div className="data-table" role="table">
          <div className="data-table__head data-table__head--admins" role="row">
            <span role="columnheader">Nome</span>
            <span role="columnheader">Usuário</span>
            <span role="columnheader">Papel</span>
            <span role="columnheader">Último login</span>
            <span role="columnheader">Ações</span>
          </div>

          {(data ?? []).map((account) => {
            const isSelf = account.id === me?.id;
            return (
              <div className="data-table__row data-table__row--admins" role="row" key={account.id}>
                <span className="cell" role="cell">
                  {account.displayName}
                  {isSelf ? <Pill>você</Pill> : null}
                  {!account.isActive ? <Pill tone="danger">inativo</Pill> : null}
                  {account.mustChangePassword ? <Pill tone="warn">senha temporária</Pill> : null}
                </span>
                <span className="cell cell--dim" role="cell">
                  @{account.username}
                </span>
                <span className="cell" role="cell">
                  <Pill tone={account.role === 'super_admin' ? 'accent' : 'neutral'}>
                    {account.role === 'super_admin' ? 'super admin' : 'admin'}
                  </Pill>
                </span>
                <span className="cell" role="cell">
                  {relativeTime(account.lastLoginAt)}
                </span>
                <span className="cell cell--actions" role="cell">
                  {/* Self-actions are hidden here, but the server rejects them
                      independently (CANNOT_DELETE_SELF / USE_CHANGE_PASSWORD). */}
                  <button
                    type="button"
                    className="btn btn--sm"
                    disabled={busy || isSelf}
                    onClick={() => setConfirmReset(account)}
                  >
                    Redefinir senha
                  </button>
                  <button
                    type="button"
                    className="btn btn--sm"
                    disabled={busy || isSelf}
                    onClick={() =>
                      void run(async () => {
                        await updateAdmin(account.id, {
                          role: account.role === 'super_admin' ? 'admin' : 'super_admin',
                        });
                      })
                    }
                  >
                    {account.role === 'super_admin' ? 'Rebaixar' : 'Promover'}
                  </button>
                  <button
                    type="button"
                    className="btn btn--sm btn--danger"
                    disabled={busy || isSelf}
                    onClick={() => setConfirmDelete(account)}
                  >
                    Excluir
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      </AsyncBoundary>

      {creating ? (
        <CreateAdminModal
          onClose={() => setCreating(false)}
          onCreated={(password) => {
            setCreating(false);
            reload();
            if (password) setSecret({ title: 'Administrador criado', value: password });
          }}
        />
      ) : null}

      {secret ? (
        <Modal title={secret.title} onClose={() => setSecret(null)}>
          <SecretReveal secret={secret.value} />
          <div className="modal__actions">
            <button type="button" className="btn btn--primary" onClick={() => setSecret(null)}>
              Já copiei
            </button>
          </div>
        </Modal>
      ) : null}

      {confirmReset ? (
        <ConfirmDialog
          title="Redefinir senha"
          confirmLabel="Gerar nova senha"
          busy={busy}
          message={
            <p>
              Uma senha temporária será gerada para{' '}
              <strong>{confirmReset.displayName}</strong>. As sessões abertas dessa conta serão
              encerradas imediatamente.
            </p>
          }
          onCancel={() => setConfirmReset(null)}
          onConfirm={() =>
            void run(async () => {
              const result = await resetAdminPassword(confirmReset.id);
              setConfirmReset(null);
              setSecret({ title: 'Senha redefinida', value: result.temporaryPassword });
            })
          }
        />
      ) : null}

      {confirmDelete ? (
        <ConfirmDialog
          title="Excluir administrador"
          confirmLabel="Excluir"
          confirmPhrase={confirmDelete.username}
          busy={busy}
          message={
            <p>
              A conta <strong>{confirmDelete.displayName}</strong> será removida e suas sessões
              encerradas. A ação não pode ser desfeita.
            </p>
          }
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() =>
            void run(async () => {
              await deleteAdmin(confirmDelete.id);
              setConfirmDelete(null);
            })
          }
        />
      ) : null}
    </div>
  );
}

function CreateAdminModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (temporaryPassword?: string) => void;
}): React.JSX.Element {
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<AdminRole>('admin');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await createAdmin({
        username: username.trim().toLowerCase(),
        displayName: displayName.trim(),
        role,
      });
      onCreated(result.temporaryPassword);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível criar.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Novo administrador" onClose={onClose}>
      <form onSubmit={(e) => void onSubmit(e)}>
        <label className="field">
          <span>Nome</span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={64}
            autoFocus
            required
          />
        </label>
        <label className="field">
          <span>Usuário</span>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="apenas letras minúsculas, dígitos, . _ -"
            required
          />
        </label>
        <label className="field">
          <span>Papel</span>
          <select value={role} onChange={(e) => setRole(e.target.value as AdminRole)}>
            <option value="admin">admin</option>
            <option value="super_admin">super admin</option>
          </select>
        </label>

        <p className="field__hint">
          Uma senha temporária será gerada e exibida uma única vez. A pessoa precisará trocá-la
          no primeiro acesso.
        </p>

        {error ? <div className="form-error">{error}</div> : null}

        <div className="modal__actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? 'Criando…' : 'Criar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
